import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchAll } from '../../lib/save';
import { useToast } from '../../components/ToastContext';
import { myWarehouseIds } from '../../lib/assignments';
import CountViewOnlyModal from '../../components/CountViewOnlyModal';
import AddLocationModal from '../../components/AddLocationModal';
import EditRequestModal from '../../components/EditRequestModal';
import MyCountsView from './MyCountsView';
import PageLoading from '../../components/PageLoading';

/*
  My Counts, for anybody who did not already load their own counts.

  **There were two of these and they were copies of each other**, one in
  `pages/admin/MyCounts.jsx` and one written inline in `ManagerDashboard.jsx`.
  They had already drifted, and the drift was live: the manager's built its
  per-location figures without `lines` and `counted`, so **every card on a
  manager's My Counts said "No lines on this sheet yet"** whether it was true or
  not. Nothing raised anything; the card printed the sentence it had.

  A rep does not use this one. `RepDashboard.jsx` already holds these counts for
  its Home, so it renders `MyCountsView` with what it has rather than asking the
  database for the same rows twice.
*/
export default function MyCounts({ cycle, profile, navigate }) {
  const toast = useToast();
  const [counts, setCounts]                     = useState([]);
  const [lineItems, setLineItems]               = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [pendingRequests, setPendingRequests]   = useState({});
  const [editRequestModal, setEditRequestModal] = useState(null);
  const [showAddLocation, setShowAddLocation]   = useState(false);
  const [viewOnlyCount, setViewOnlyCount]       = useState(null);

  useEffect(() => { loadData(); }, [cycle, profile]); // eslint-disable-line
  // Somebody who counts in another tab and comes back should see it here.
  // **Quietly**: it used to put the page back to its loading outline, which
  // threw away the search, the filter and any open box (the audit after the
  // rebrand, issue 20, 18 September 2026).
  useEffect(() => {
    const onFocus = () => { loadData({ quiet: true }); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [cycle, profile]); // eslint-disable-line

  async function loadData({ quiet = false } = {}) {
    if (!cycle || !profile?.id) { setLoading(false); return; }
    if (!quiet) setLoading(true);

    // My Counts is the locations I am assigned to, joined to this count's sheets.
    const myIds = await myWarehouseIds(profile.id);

    let countData = [];
    if (myIds.length > 0) {
      // **`display_name` and `region_prefix` are what the card prints.** The
      // raw name carries the territory code on the front of it, so a card
      // without these two says "DFW" twice.
      const { data } = await supabase
        .from('inventory_counts')
        .select('id, status, submitted_at, rep_id, owner:profiles!inventory_counts_rep_id_fkey(full_name), warehouse:warehouses(id, name, display_name, region_prefix, allows_concurrent_counting, region:regions(name))')
        .eq('cycle_id', cycle.id)
        .in('warehouse_id', myIds)
        .order('id');
      countData = data || [];
    }
    setCounts(countData);

    if (countData.length) {
      const ids = countData.map(c => c.id);
      const { data: pendingTodos } = await supabase
        .from('todos').select('count_id')
        .in('count_id', ids)
        .eq('todo_type', 'edit_request')
        .eq('is_complete', false);
      const pending = {};
      for (const t of pendingTodos || []) pending[t.count_id] = true;
      setPendingRequests(pending);

      /*
        **Read a page at a time.** A single request brings back about a thousand
        rows and says nothing about the rest, and one rep's forty locations can
        carry more than that on their own: three of Michelle Chelette's sheets
        hold 111, 300 and 89 lines between them. Asking once would have left
        her last locations reading "No lines on this sheet yet" with no way to
        tell that from the truth.
      */
      const { data: lineData } = await fetchAll(() => supabase
        .from('count_line_items')
        .select('inventory_count_id, quantity, item:item_catalog_id(cost_per_item)')
        .in('inventory_count_id', ids)
        .order('id'));
      setLineItems(lineData || []);
    } else {
      setPendingRequests({});
      setLineItems([]);
    }

    setLoading(false);
  }

  if (loading) return <PageLoading rows={6} />;

  const stats = {
    not_started: counts.filter(c => c.status === 'not_started').length,
    in_progress:  counts.filter(c => c.status === 'in_progress').length,
    submitted:    counts.filter(c => c.status === 'submitted').length,
    approved:     counts.filter(c => c.status === 'approved').length,
  };

  const countValueMap = valueMapOf(lineItems);

  return (
    <>
      <MyCountsView
        cycle={cycle}
        counts={counts}
        stats={stats}
        countValueMap={countValueMap}
        pendingRequests={pendingRequests}
        profile={profile}
        onOpen={c => navigate('/count/' + c.id)}
        onRequestEdit={c => setEditRequestModal(c)}
        onViewOnly={c => setViewOnlyCount(c)}
        onAddLocation={() => setShowAddLocation(true)}
      />
      {editRequestModal && (
        <EditRequestModal
          count={editRequestModal}
          profile={profile}
          toast={toast}
          onClose={() => setEditRequestModal(null)}
          onSuccess={countId => setPendingRequests(p => ({ ...p, [countId]: true }))}
        />
      )}
      {viewOnlyCount && (
        <CountViewOnlyModal
          count={viewOnlyCount}
          profile={profile}
          toast={toast}
          // An admin takes a sheet back rather than asking the admins for it
          // (issue 10, 18 September 2026: this was never passed).
          isAdmin={profile?.role === 'admin'}
          onChanged={loadData}
          onClose={() => setViewOnlyCount(null)}
        />
      )}
      {showAddLocation && (
        <AddLocationModal
          profile={profile}
          cycle={cycle}
          toast={toast}
          onClose={() => setShowAddLocation(false)}
          onSuccess={loadData}
        />
      )}
    </>
  );
}

/*
  What each sheet holds, per location.

  **`lines` and `counted` are what rule 9 needs**, and the manager's copy of this
  had neither. Units and value alone cannot tell a sheet nobody has opened from a
  sheet counted honestly as all zeros: both come out as nothing, and the card
  then says the same sentence about two opposite facts.

  Exported so `RepDashboard.jsx`, which loads its own counts for Home, builds the
  map the same way rather than keeping a third version of it.
*/
export function valueMapOf(lineItems) {
  const map = {};
  for (const i of lineItems || []) {
    const id = i.inventory_count_id;
    if (!map[id]) map[id] = { units: 0, value: 0, lines: 0, counted: 0 };
    map[id].units += (i.quantity || 0);
    map[id].value += (i.quantity || 0) * (i.item?.cost_per_item || 0);
    map[id].lines += 1;
    if (i.quantity !== null && i.quantity !== undefined) map[id].counted += 1;
  }
  return map;
}
